import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { describeAuditEntry, type AuditEntryDto, type TripDto, type TripStatus } from "@tc/shared";
import { Card, Icon, Timeline, type IconName } from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { STATUS_KEY } from "@/features/trips/status";
import { notesApi } from "./api";

/**
 * Per-trip audit tab (TW-3457) — the real logged events for the trip (status transitions, proposal
 * sends, manifest assignments, roster edits, invoice actions, exports), newest first, as a timeline
 * with an icon per event. The row → {icon, label} mapping is the shared, unit-tested
 * `describeAuditEntry`; here we only localize the label (the trip-status entry's status is localized
 * via the same `status.*` keys the rest of the UI uses). Back-office only (server-gated on
 * `audit.read`; the tab is not mounted for club users).
 */
export function AuditTab({ trip }: { trip: TripDto }) {
  const { t } = useTranslation();
  const fmt = useFormatters();

  const [entries, setEntries] = useState<AuditEntryDto[] | null>(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      setEntries(await notesApi.listAudit(trip.id));
    } catch {
      setError(true);
    }
  }, [trip.id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <Card>
        <div className="empty">{t("audit.loadError")}</div>
      </Card>
    );
  }

  if (entries && entries.length === 0) {
    return (
      <Card>
        <div className="empty">
          <div className="empty-ic">
            <Icon name="clock" size={24} />
          </div>
          <div style={{ fontWeight: 600, marginTop: 4 }}>{t("audit.empty")}</div>
        </div>
      </Card>
    );
  }

  return (
    <div data-fullw="audit">
      <p className="muted" style={{ fontSize: 13.5, marginTop: 0, marginBottom: 18 }}>
        {t("audit.subtitle")}
      </p>
      <Timeline
        variant="audit"
        items={(entries ?? []).map((entry) => {
          const d = describeAuditEntry(entry);
          const params =
            d.kind === "tripStatus" && d.labelParams.status
              ? { ...d.labelParams, status: t(STATUS_KEY[d.labelParams.status as TripStatus] ?? d.labelParams.status) }
              : d.labelParams.reason
                ? // An on-behalf decision (TRAV-74): localize the reason key into its phrase.
                  { ...d.labelParams, reason: t(`proposal.onBehalf.reasonPhrase.${d.labelParams.reason}`) }
                : d.labelParams;
          // The operator's free-text context on an on-behalf decision rides alongside the actor.
          const note =
            d.labelParams.reason && typeof entry.after?.note === "string" && entry.after.note ? entry.after.note : null;
          const actor = entry.actorName ?? t("audit.system");
          return {
            key: entry.id,
            node: <Icon name={d.icon as IconName} size={13} />,
            title: t(d.labelKey, params),
            sub: note ? `${actor} · “${note}”` : actor,
            meta: fmt.date(entry.createdAt),
          };
        })}
      />
    </div>
  );
}
